import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { QueryClientContext } from '@tanstack/react-query';
import axios from 'axios';
import moment from 'moment-timezone';
import { useContext, useMemo, useState } from 'react';
import { PlainDate } from 'temporal-luxon';
import { FirebaseAppContext } from 'libs.firebase_react';
import { DatePicker, FormSections, useLocalizeMessage, ModalWindow, Text, useToastNotification, Badge, InlineNotification, RadioGroup, RadioGroupOption, Select, Spinner, } from 'libs.nucleus';
import { useDateTime } from 'libs.react';
import { getEnvironmentAgeHours, TIME_CONSTANTS } from '../../utils/analytics/environment_analytics.utils';
const MAX_RELATIVE_TIME_TRAVEL_DAYS = 100;
const DEFAULT_OPTION = {
    label: '0',
    value: '0',
};
var ServerTimeType;
(function (ServerTimeType) {
    ServerTimeType["Absolute"] = "absolute";
    ServerTimeType["Relative"] = "relative";
})(ServerTimeType || (ServerTimeType = {}));
// FIXME: Replace useDateTime with useDateTimeFormat and remove moment usage in favor of Temporal APIs.
export const ServerTimeModal = ({ onClose, studyEnv, serverDateTime, studyId, cortexUrlOverride, laneLabel, }) => {
    const translate = useLocalizeMessage();
    const queryClient = useContext(QueryClientContext);
    const { logEvent } = useContext(FirebaseAppContext);
    const { addNotification } = useToastNotification();
    const { parseDateTimeShort } = useDateTime();
    const [isLoading, setIsLoading] = useState(false);
    const serverTimeTypeOptions = [
        {
            label: translate('Absolute'),
            value: ServerTimeType.Absolute,
        },
        {
            label: translate('Relative'),
            value: ServerTimeType.Relative,
        },
    ];
    const [simulationTimeForm, setSimulationTimeForm] = useState({
        timeType: serverTimeTypeOptions[0].value,
        simulationDate: undefined,
        days: DEFAULT_OPTION,
        hours: DEFAULT_OPTION,
        minutes: DEFAULT_OPTION,
        ampm: {
            label: 'AM',
            value: 'AM',
        },
    });
    const momentTimezone = moment.tz.guess();
    const options = useMemo(() => {
        const hours = Array.from({ length: 13 });
        const relativeHours = Array.from({ length: 25 });
        const minutes = Array.from({ length: 61 });
        const days = Array.from({ length: MAX_RELATIVE_TIME_TRAVEL_DAYS + 1 });
        const convertToSelectOption = (arr) => {
            return arr.map((_, index) => ({
                label: index.toString(),
                value: index.toString(),
            }));
        };
        return {
            hours: convertToSelectOption(hours),
            relativeHours: convertToSelectOption(relativeHours),
            minutes: convertToSelectOption(minutes),
            days: convertToSelectOption(days),
        };
    }, []);
    const formatTimeDifference = (date, from) => {
        const expiresAt = moment(date).tz(momentTimezone);
        const now = moment(from).tz(momentTimezone);
        const diff = expiresAt.diff(now, 'minutes');
        const hour = Math.max(Math.floor(diff / 60), 0);
        const minute = Math.max(diff % 60, 0);
        const day = Math.max(Math.floor(hour / 24), 0);
        const hourInDay = Math.max(hour % 24, 0);
        if (day > 0) {
            return translate('{day}d {hour}h {minute}m', { day, hour: hourInDay, minute });
        }
        if (hour > 0) {
            return translate('{hour}h {minute}m', { hour, minute });
        }
        return translate('{minute}m', { minute });
    };
    const remainingTime = useMemo(() => {
        if (serverDateTime) {
            return formatTimeDifference(serverDateTime);
        }
        return '';
    }, [serverDateTime]);
    const getSimulationDate = () => {
        if (simulationTimeForm.timeType === ServerTimeType.Absolute) {
            const date = moment(simulationTimeForm.simulationDate?.toString()).tz(momentTimezone);
            const hour = simulationTimeForm.hours.label;
            const minute = simulationTimeForm.minutes.label;
            const ampm = simulationTimeForm.ampm.label;
            const hourValue = parseInt(hour) % 12;
            return date.set({
                hour: ampm === 'PM' ? hourValue + 12 : hourValue,
                minute: parseInt(minute) % 60,
            });
        }
        const hour = simulationTimeForm.hours.label;
        const minute = simulationTimeForm.minutes.label;
        const day = simulationTimeForm.days.label;
        const date = moment(serverDateTime)
            .tz(momentTimezone)
            .add(parseInt(day), 'days')
            .add(parseInt(hour), 'hours')
            .add(parseInt(minute), 'minutes');
        return date;
    };
    const newSimulationTime = useMemo(() => {
        const date = getSimulationDate();
        if (simulationTimeForm.timeType === ServerTimeType.Absolute) {
            return translate('+{date}', {
                date: formatTimeDifference(date.toISOString()),
            });
        }
        return date.format('MMM DD, YYYY hh:mm A Z z');
    }, [simulationTimeForm]);
    const saveDisabled = useMemo(() => {
        if (isLoading) {
            return true;
        }
        if (simulationTimeForm.timeType === ServerTimeType.Absolute) {
            return !simulationTimeForm.simulationDate || !simulationTimeForm.hours || !simulationTimeForm.minutes;
        }
        return !simulationTimeForm.days || !simulationTimeForm.hours || !simulationTimeForm.minutes;
    }, [isLoading, simulationTimeForm]);
    const saveServerTime = async () => {
        if (isLoading || saveDisabled) {
            return;
        }
        try {
            setIsLoading(true);
            const serverTime = moment(serverDateTime).tz(momentTimezone);
            const simulationDate = getSimulationDate();
            const diff = simulationDate.diff(serverTime, 'seconds');
            if (diff <= 0) {
                addNotification({
                    title: translate('Error setting time'),
                    subtitle: translate('The new simulation time must be greater than the current time'),
                    type: 'error',
                });
                return;
            }
            const cortexClusterUrl = cortexUrlOverride || studyEnv.cortexURL;
            const isoDate = simulationDate.toISOString();
            const { data } = await axios.post(`${cortexClusterUrl}/settime/${isoDate}`);
            if (data.shift === '+0m') {
                throw new Error(translate('Simulation time was not updated, the selected server time is in the past'));
            }
            const eventProperties = {
                environment_id: studyEnv.id,
                study_id: studyId,
                update_type: simulationTimeForm.timeType,
                previous_simulation_time: serverDateTime,
                new_simulation_time: isoDate,
                time_delta_hours: diff / TIME_CONSTANTS.SECONDS_PER_HOUR,
                time_delta_days: diff / TIME_CONSTANTS.SECONDS_PER_DAY,
                environment_age_hours: getEnvironmentAgeHours(studyEnv.createdAt),
            };
            if (simulationTimeForm.timeType === ServerTimeType.Relative) {
                eventProperties.days_added = parseInt(simulationTimeForm.days.value);
                eventProperties.hours_added = parseInt(simulationTimeForm.hours.value);
                eventProperties.minutes_added = parseInt(simulationTimeForm.minutes.value);
            }
            logEvent('tt_simulation_time_updated', eventProperties);
            await queryClient?.invalidateQueries({
                queryKey: ['entities', studyEnv.entityId, 'studies', studyId, 'environment', `${studyEnv.id}`, 'serverTime'],
            });
            addNotification({
                title: translate('Simulation time was updated successfully.'),
                type: 'success',
            });
            onClose();
        }
        catch (error) {
            addNotification({
                title: translate('Failed to update simulation time'),
                subtitle: error instanceof Error ? error.message : 'Unknown error',
                type: 'error',
            });
        }
        finally {
            setIsLoading(false);
        }
    };
    const savePrimaryButton = {
        label: translate('Update'),
        onClick: saveServerTime,
        disabled: saveDisabled,
    };
    const cancelSecondaryButton = {
        label: translate('Cancel'),
        onClick: onClose,
    };
    const onSubmit = (event) => {
        event.preventDefault();
        saveServerTime();
    };
    const onFieldChange = (field, value) => {
        setSimulationTimeForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };
    const formSections = [
        {
            title: translate('Simulation time'),
            content: (_jsxs("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: [_jsxs("div", { className: 'flex gap-2', children: [_jsx(Text, { size: 'sm', className: 'text-text-primary font-medium flex-shrink-0', children: translate('Current simulation time:') }), _jsx(Text, { size: 'sm', className: 'text-text-secondary', children: parseDateTimeShort(serverDateTime, {
                                    ignoreTimeZone: false,
                                    showTimeZone: true,
                                }) })] }), _jsxs(Text, { size: 'sm', className: 'text-text-primary font-medium flex gap-2', as: 'div', children: [translate('Difference with current time:'), _jsx(Badge, { label: translate('+{remainingTime}', {
                                    remainingTime,
                                }), startIcon: {
                                    name: 'time',
                                    size: 'sm',
                                    color: 'current',
                                } })] }), _jsxs("div", { children: [_jsx(Text, { size: 'sm', className: 'text-text-primary font-medium', children: translate('Date selection type') }), _jsx(RadioGroup, { onChange: (value) => {
                                    onFieldChange('timeType', value);
                                }, value: simulationTimeForm.timeType, children: serverTimeTypeOptions.map((option) => (_jsx(RadioGroupOption, { option: {
                                        value: option.value,
                                        label: option.label,
                                    } }, option.value))) })] }), _jsxs("div", { className: 'flex flex-col', children: [_jsx(Text, { size: 'sm', className: 'text-text-primary font-medium', children: translate('New simulation time') }), _jsxs("div", { className: 'flex gap-2', children: [simulationTimeForm.timeType === ServerTimeType.Absolute && (_jsx(DatePicker
                                    // @ts-expect-error Allow label to use JSX element
                                    , { 
                                        // @ts-expect-error Allow label to use JSX element
                                        label: _jsx(Text, { size: 'xs', className: 'text-text-secondary font-light mb-[0.2rem]', as: 'div', children: translate('The setting is in your own local timezone') }), id: 'simulation-date', startDate: PlainDate.from(moment().tz(momentTimezone).toISOString()), value: simulationTimeForm.simulationDate, onChange: (date) => {
                                            onFieldChange('simulationDate', date);
                                        }, required: true, hideRequiredIndicator: true })), simulationTimeForm.timeType === ServerTimeType.Relative && (_jsx(Select, { label: translate('Day'), onChange: (option) => {
                                            onFieldChange('days', option);
                                        }, required: true, options: options.days, value: simulationTimeForm.days, width: 'full' })), _jsx(Select, { label: translate('Hour'), onChange: (option) => {
                                            onFieldChange('hours', option);
                                        }, required: true, options: simulationTimeForm.timeType === ServerTimeType.Relative ? options.relativeHours : options.hours, value: simulationTimeForm.hours, width: 'full' }), _jsx(Select, { label: translate('Minute'), onChange: (option) => {
                                            onFieldChange('minutes', option);
                                        }, required: true, options: options.minutes, value: simulationTimeForm.minutes, width: 'full' }), simulationTimeForm.timeType === ServerTimeType.Absolute && (_jsx(Select, { label: translate('AM/PM'), onChange: (option) => {
                                            onFieldChange('ampm', option);
                                        }, required: true, options: [
                                            { label: 'AM', value: 'AM' },
                                            { label: 'PM', value: 'PM' },
                                        ], value: simulationTimeForm.ampm, width: 'full' }))] })] }), _jsxs(Text, { size: 'sm', className: 'text-text-primary font-medium flex gap-2', as: 'div', children: [translate('Preview of simulation time:'), _jsx(Badge, { label: newSimulationTime, startIcon: {
                                    name: 'time',
                                    size: 'sm',
                                    color: 'current',
                                } })] })] })),
        },
    ];
    return (_jsxs(ModalWindow, { title: laneLabel
            ? translate('Update simulation time ({lane})', { lane: laneLabel })
            : translate('Update simulation time'), width: 'full', footerPrimaryActionButton: savePrimaryButton, footerSecondaryActionButtons: [cancelSecondaryButton], isOpen: true, closeWindow: onClose, children: [isLoading && _jsx(Spinner, { wrapper: 'full' }), _jsx(InlineNotification, { title: translate('Time travel only travels forward'), subtitle: translate('Time travel functionality only allows travelling forward in time. If you advance simulation time and then need to go backward, you will need to create a new sandbox. You can then start a new Time travel to go forward to the time you need.') }), _jsx("form", { className: 'min-h-[30rem] mt-6', autoComplete: 'off', onSubmit: onSubmit, children: _jsx(FormSections, { sections: formSections }) })] }));
};
